
// import { useState, useEffect, useRef, useMemo } from "react";

// export const useCoinCapWebSocket = () => {
//   const [message, setMessage] = useState(null);
//   const webSocketRef = useRef(null);
//   const url = "wss://wss.coincap.io/prices?assets=bitcoin";
//   const memoizedUrl = useMemo(() => url, []);

//   useEffect(() => {
//     let keepAliveInterval;
//     const connectWebSocket = () => {
//       if (webSocketRef.current && webSocketRef.current.readyState !== WebSocket.CLOSED) {
//         return;
//       }
//       webSocketRef.current = new WebSocket(memoizedUrl);
//       webSocketRef.current.onopen = () => {
//         // console.log("WebSocket connection opened:", memoizedUrl);

//         keepAliveInterval = setInterval(() => {
//           if (webSocketRef.current.readyState === WebSocket.OPEN) {
//             webSocketRef.current.send('ping');
//           }
//         }, 9000); // Send a ping message
//       };

//       webSocketRef.current.onmessage = (event) => {
//         setMessage(event.data);
//       };

//       webSocketRef.current.onerror = (event) => {
//         console.error("WebSocket error:", event);
//       };

//       webSocketRef.current.onclose = (event) => {
//         // console.log("WebSocket connection closed:", event);
//         // console.log("Reason:", event.reason);
//         clearInterval(keepAliveInterval);
//         setTimeout(() => {
//           // console.log("Attempting to reconnect...");
//           connectWebSocket();
//         }, 9000); // Change to 9 seconds or a higher value
//       };
//     };
//     connectWebSocket();
//     return () => {
//       if (webSocketRef.current) {
//         webSocketRef.current.close();
//       }
//       clearInterval(keepAliveInterval);
//     };
//   }, [memoizedUrl]);
//   return { message };
// };



import { useState, useEffect, useRef, useMemo } from "react";
import { throttle } from './throttle';
import { createReconnectManager } from './websocketReconnect.utils';

export const useCoinCapWebSocket = () => {
  const [message, setMessage] = useState(null);
  const webSocketRef = useRef(null);
  const reconnectManagerRef = useRef(null);
  const url = "wss://ws-feed.exchange.coinbase.com";
  const memoizedUrl = useMemo(() => url, []);
  
  // Throttle message updates to once per second (1000ms)
  const throttledSetMessage = useMemo(
    () => throttle((msg) => {
      setMessage(msg);
    }, 1000),
    []
  );

  useEffect(() => {
    let keepAliveInterval;
    
    // Create reconnect manager
    reconnectManagerRef.current = createReconnectManager();
    
    const connectWebSocket = () => {
      if (webSocketRef.current && webSocketRef.current.readyState !== WebSocket.CLOSED) {
        return;
      }
      
      webSocketRef.current = new WebSocket(memoizedUrl);
      
      webSocketRef.current.onopen = () => {
        // Reset reconnect attempts on successful connection
        reconnectManagerRef.current.resetAttempts();
        
        // Subscribe to BTC-USD ticker
        const subscribeMsg = {
          type: 'subscribe',
          product_ids: ['BTC-USD'],
          channels: ['ticker']
        };
        
        webSocketRef.current.send(JSON.stringify(subscribeMsg));
        
        keepAliveInterval = setInterval(() => {
          if (webSocketRef.current.readyState === WebSocket.OPEN) {
            webSocketRef.current.send('ping');
          }
        }, 9000); // Send a ping message
      };
      
      webSocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ticker') {
            // Format the data to match the expected format in App.js
            // App.js expects a JSON string with { bitcoin: price }
            const formattedData = JSON.stringify({ bitcoin: data.price });
            throttledSetMessage(formattedData);
          }
        } catch (error) {
          // console.error("Error parsing WebSocket message:", error);
        }
      };
      
      webSocketRef.current.onerror = (event) => {
        // console.error("WebSocket error:", event);
      };
      
      webSocketRef.current.onclose = (event) => {
        clearInterval(keepAliveInterval);
        
        // Don't attempt to reconnect if we're shutting down
        if (event.code === 1000 || event.wasClean) {
          return;
        }
        
        // Use reconnect manager for exponential backoff
        reconnectManagerRef.current.scheduleReconnect(connectWebSocket);
      };
    };
    
    // Handle visibility change events (when PWA returns from background)
    const handleVisibilityChange = () => {
      reconnectManagerRef.current.handleVisibilityReconnect(webSocketRef, connectWebSocket, {
        minDelay: 5000,  // Wait at least 5 seconds between reconnects
        reconnectDelay: 1000  // 1-2 second delay when reconnecting
      });
    };
    
    // Handle focus events as a fallback
    const handleFocus = () => {
      handleVisibilityChange();
    };
    
    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    connectWebSocket();
    
    return () => {
      // Remove event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      
      // Clear any pending reconnects
      reconnectManagerRef.current.clearPendingReconnect();
      
      if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
        webSocketRef.current.close(1000, 'Component unmount');
      }
      clearInterval(keepAliveInterval);
      // Cancel any pending throttled function calls
      throttledSetMessage.cancel();
    };
  }, [memoizedUrl, throttledSetMessage]);
  
  return { message };
};